import { CallReason, roomEvents } from "@closerplatform/closer-sdk";
import { MeetingStatus } from "@fidget/common/models/meeting-status";
import { SnapshotIn } from "mobx-state-tree";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { CallEndedPayload, FormPayload } from "@fidget/common/models/chat/chatItems";
import { MessageTag } from "@fidget/common/models/messageTag";
import { ChatItem } from "./chat/ChatItem";
import { ChatItemStatus } from "./chat/ChatItemStatus";
import { ChatItemType } from "./chat/ChatItemType";
import {
  CallEventChatItem,
  CobrowsingEventChatItem,
  CTAEventChatItem,
  EmptyChatItem,
  FakeUserTextChatItem,
  MeetingChatItem,
  MeetingSchedulerChatItem,
  NoteChatItem,
  NotificationChatItem,
  StatusChangeChatItem,
  TagsChangeChatItem,
  TopicsChangeChatItem,
  UserEventChatItem,
  UserTextChatItem,
  WidgetFormEventChatItem
} from "./chat/items";
import RoomEvent = roomEvents.RoomEvent;
import { ConversationStatus, UrlParameter } from "@closerplatform/spinner-openapi";
import { SpinnerService } from "../../services/spinner/spinnerService";
import { RoomSummaryChatItem } from "./chat/items/RoomSummaryChatItem";
import { isPredefinedMessageConfigContext } from "@fidget/common/models/shared/spinner/types/messageWidgets/WidgetFormSubmissionWithWidget";
import { applyUrlParameter } from "@fidget/common/models/chat/utils";
import { hardcodedCloserBotProfile } from "@fidget/common/models/hacks";

export type IntentData = {
  label: string;
  probability: number;
};

/**
 * See Intent model in src/agent/models/room/chat/ChatItem.ts
 * TODO: use spinner type for Intent source data
 */
export function intentResourceDataToSnapshot(data: IntentData) {
  return {
    type: data.label,
    probability: data.probability
  };
}

export async function roomEventToChatItemSnapshot(
  ev: RoomEvent,
  spinnerService: SpinnerService
): Promise<SnapshotIn<typeof ChatItem>> {
  if (roomEvents.MessageSent.isMessageSent(ev)) {
    return messageSentToUserTextChatItem(ev);
  }
  if (roomEvents.FakeMessageSent.isFakeMessageSent(ev)) {
    return fakeMessageSentToFakeUserTextChatItem(ev);
  }
  if (roomEvents.CustomMessageSent.isCustomMessageSent(ev)) {
    switch (ev.subtag) {
      case MessageTag.CONTACT_ENQUIRY:
      case MessageTag.CONTACT_ENQUIRY_WITHOUT_GDPR:
      case MessageTag.ALL_FOLLOWERS_REMOVED:
      case MessageTag.COBROWSING_ENDED:
      case MessageTag.CALL_STARTED:
      case MessageTag.FILE_DOWNLOADED:
      case MessageTag.LINK_CLICKED:
        return eventToEmptyChatItem(ev);

      case MessageTag.LEKTA_MESSAGE:
        return lektaMessageToChatItem(ev);

      case MessageTag.MESSAGE_WITH_ATTACHMENTS:
      case MessageTag.CALL_PROPOSAL:
      case MessageTag.COBROWSING_PROPOSAL:
      case MessageTag.COBROWSING_ACCEPTATION:
        return customMessageSentToUserTextChatItem(ev);

      case MessageTag.ROOM_SUMMARY:
        return customMessageSentToRoomSummary(ev);

      case MessageTag.NOTE:
        return customMessageSentToNoteChatItem(ev);

      case MessageTag.STATUS_CHANGED:
        return customMessageSentToStatusChangeChatItem(ev);

      case MessageTag.ASSIGNEE_CHANGED:
      case MessageTag.ASSIGNEE_REMOVED:
      case MessageTag.FOLLOWER_ADDED:
      case MessageTag.FOLLOWER_REMOVED:
        return customMessageSentToUserEventChatItem(ev, ev.subtag);

      case MessageTag.CALL_ANSWERED:
        return customMessageSentToCallEventChatItem(ev, ev.subtag);

      case MessageTag.COBROWSING_SESSION_STARTED:
      case MessageTag.COBROWSING_SESSION_ENDED:
        return customMessageSentToCobrowsingEventChatItem(ev, ev.subtag);

      case MessageTag.FORM_SUBMITTED:
      case MessageTag.MESSAGE_WITH_FORM:
        return customMessageSentToWidgetFormEventChatItem(ev, ev.subtag, spinnerService);

      case MessageTag.CALL_UNSUCCESSFUL:
        return customMessageSentToCTAEventChatItem(ev, ev.subtag);

      case MessageTag.CALL_ENDED:
        const payload = ev.context as CallEndedPayload;
        if (payload.reason === CallReason.TimeLimitExceeded) {
          return customMessageSentToCTAEventChatItem(ev, ev.subtag);
        }

        return customMessageSentToCallEventChatItem(ev, ev.subtag);

      case MessageTag.MEETING_SCHEDULED:
      case MessageTag.MEETING_RESCHEDULED:
      case MessageTag.MEETING_CANCELLED:
        return customMessageSentToMeetingChatItem(ev);

      case MessageTag.MEETING:
        return customMessageSentMeetingSchedulerChatItem(ev);

      case MessageTag.LEAD_TAGS_UPDATE:
        return customMessageSentToTagsChangeChatItem(ev);

      case MessageTag.LEAD_TOPICS_UPDATE:
        return customMessageSentToTopicsChangeChatItem(ev);

      case MessageTag.EXTERNAL_MESSAGE:
      default:
        return customMessageSentToNotificationChatItem(ev);
    }
  }

  return eventToEmptyChatItem({
    messageId: `${ev.authorId}-${ev.roomId}-${ev.timestamp}`,
    timestamp: ev.timestamp
  });
}

const messageSentToUserTextChatItem = (
  ev: roomEvents.MessageSent
): SnapshotIn<typeof UserTextChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.UserText,
    text: ev.message,
    user: ev.authorId,
    status: ChatItemStatus.SENT,
    intents: ev.context.intents?.map(intentResourceDataToSnapshot)
  };
};

const fakeMessageSentToFakeUserTextChatItem = (
  ev: roomEvents.FakeMessageSent
): SnapshotIn<typeof FakeUserTextChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.FakeUserText,
    text: intl.formatMessage("plan.upgrade.chatMessage"),
    user: ev.authorId,
    status: ChatItemStatus.SENT
  };
};

const eventToEmptyChatItem = (
  ev: Pick<roomEvents.CustomMessageSent, "messageId" | "timestamp">
): SnapshotIn<typeof EmptyChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.Empty
  };
};

const lektaMessageToChatItem = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof UserTextChatItem | typeof EmptyChatItem> => {

  const textMessage = ev.context.message || ev.message;

  if (!textMessage) {
    return eventToEmptyChatItem(ev);
  }

  return customMessageSentToUserTextChatItem(ev);

};

const customMessageSentToUserTextChatItem = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof UserTextChatItem> => {

  const isCloserBot = Object.keys(ev.context || {}).length === 0;

  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.UserText,
    user: isCloserBot ? hardcodedCloserBotProfile.id : ev.authorId,
    text: ev.context.message || ev.message,
    status: ChatItemStatus.SENT,
    attachments: ev.context.attachments,
    intents: ev.context.intents?.map(intentResourceDataToSnapshot)
  };
};

const customMessageSentToNoteChatItem = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof NoteChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.Note,
    user: ev.context.user,
    text: ev.context.body
  };
};

const customMessageSentToStatusChangeChatItem = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof StatusChangeChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.StatusChange,
    user: ev.context.agent,
    status: ev.context.status
  };
};

const customMessageSentToUserEventChatItem = (
  ev: roomEvents.CustomMessageSent,
  subtag: SnapshotIn<typeof UserEventChatItem>["subtype"]
): SnapshotIn<typeof UserEventChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.UserEvent,
    subtype: subtag,
    actor: ev.context.requesterId,
    subject: ev.context.assignee || ev.context.user
  };
};

const customMessageSentToCallEventChatItem = (
  ev: roomEvents.CustomMessageSent,
  subtag: SnapshotIn<typeof CallEventChatItem>["subtype"]
): SnapshotIn<typeof CallEventChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.CallEvent,
    subtype: subtag,
    message: ev.message,
    eventData: ev.context
  };
};

const customMessageSentToRoomSummary = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof RoomSummaryChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.RoomSummary,
    context: ev.context
  };
};

const customMessageSentToCobrowsingEventChatItem = (
  ev: roomEvents.CustomMessageSent,
  subtag: SnapshotIn<typeof CobrowsingEventChatItem>["subtype"]
): SnapshotIn<typeof CobrowsingEventChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.CobrowsingEvent,
    subtype: subtag,
    message: ev.message,
    eventData: ev.context
  };
};

const customMessageSentToWidgetFormEventChatItem = (
  ev: roomEvents.CustomMessageSent,
  subtag: SnapshotIn<typeof WidgetFormEventChatItem>["subtype"],
  spinnerService: SpinnerService
): Promise<SnapshotIn<typeof WidgetFormEventChatItem> | SnapshotIn<typeof UserTextChatItem>> => {
  return spinnerService.getWidgetForm((ev.context as FormPayload).formId).then((submission) => {

    if (isPredefinedMessageConfigContext(submission.widgetConfig)) {
      const messageWithParams = applyUrlParameter(submission.widgetConfig.message, UrlParameter.RoomId, submission.roomId);
      return {
        id: ev.messageId,
        timestamp: ev.timestamp,
        text: messageWithParams,
        type: ChatItemType.UserText,
        user: ev.authorId,
        status: ChatItemStatus.DELIVERED,
        attachments: [],
        intents: [],
      };
    }

    return {
      id: ev.messageId,
      timestamp: ev.timestamp,
      type: ChatItemType.WidgetFormEvent,
      subtype: subtag,
      message: ev.message,
      eventData: ev.context,
      submission
    };
  });
};

const customMessageSentToCTAEventChatItem = (
  ev: roomEvents.CustomMessageSent,
  subtag: SnapshotIn<typeof CTAEventChatItem>["subtype"]
): SnapshotIn<typeof CTAEventChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.CTAEvent,
    subtype: subtag,
    message: ev.message,
    eventData: ev.context
  };
};

const customMessageSentToMeetingChatItem = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof MeetingChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.Meeting,
    user: ev.authorId,
    start: ev.context.start,
    duration: ev.context.duration,
    agent: ev.context.agent,
    meetingStatus:
      ev.subtag === MessageTag.MEETING_RESCHEDULED
        ? MeetingStatus.RESCHEDULED
        : ev.subtag === MessageTag.MEETING_CANCELLED
          ? MeetingStatus.CANCELLED
          : MeetingStatus.SCHEDULED,
    changed: false // TODO: waiting for CLSR-2982 to be done
  };
};

const customMessageSentMeetingSchedulerChatItem = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof MeetingSchedulerChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.MeetingScheduler,
    token: ev.context.token,
    progressStatus: ev.context.status,
    user: ev.authorId
  };
};

const customMessageSentToTagsChangeChatItem = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof TagsChangeChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.TagsChange,
    eventData: ev.context
  };
};

const customMessageSentToTopicsChangeChatItem = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof TopicsChangeChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.TopicsChange,
    eventData: ev.context
  };
};

const customMessageSentToNotificationChatItem = (
  ev: roomEvents.CustomMessageSent
): SnapshotIn<typeof NotificationChatItem> => {
  return {
    id: ev.messageId,
    timestamp: ev.timestamp,
    type: ChatItemType.Notification,
    text: ev.message
  };
};

export const isConversationStatusClosed = (status: ConversationStatus): boolean => {
  switch (status) {
    case ConversationStatus.Solved:
    case ConversationStatus.Unsolved:
      return true;
    default:
      return false;
  }
};
